import React, { useState } from 'react';
import { Upload, Avatar, Spin , theme } from 'antd';
import { message } from '../services/toastMessage';
import type { RcFile, UploadProps } from 'antd/es/upload';
import { UserOutlined, CameraOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { uploadAvatar } from '../services/AuthenticationService';
import AvatarCropModal from './AvatarCropModal';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const DEFAULT_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const DEFAULT_MAX_SIZE_MB = 5;

export interface AvatarUploadProps {
  value?: string;
  onChange?: (url: string) => void;
  size?: number;
  disabled?: boolean;
  invalidTypeMessage?: string;
  tooLargeMessage?: string;
  uploadFailedMessage?: string;
  /**
   * Defaults to the account avatar endpoint (MediaController.UploadAvatar).
   * Pass a different one to reuse this same circular crop/zoom/rotate
   * picker for another circularly-displayed single photo -- e.g. a host's
   * ProfilePhotoOrLogo, uploaded via MediaController's generic per-domain
   * photos endpoint instead (see HostOnboardingWizard/HostProfileEditPage).
   */
  uploadFn?: (file: File) => Promise<string>;
}

/**
 * Single-photo picker with a circular crop/zoom/rotate step (AvatarCropModal)
 * between picking a file and actually uploading it. Originally built for the
 * account avatar (MediaController.UploadAvatar, the default uploadFn), and
 * general enough via the uploadFn prop to reuse for any other field that's
 * always displayed inside a circular <Avatar> elsewhere in the app. Wire it
 * inside a Form.Item with a `name` and it behaves like any other controlled
 * field via the value/onChange contract -- same pattern as the Marketplace
 * fork's PhotoUpload, just single-image and circular.
 */
const AvatarUpload: React.FC<AvatarUploadProps> = ({
  value,
  onChange,
  size = 96,
  disabled,
  invalidTypeMessage,
  tooLargeMessage,
  uploadFailedMessage,
  uploadFn = uploadAvatar,
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  // The raw picked file never gets uploaded directly -- it's only ever an
  // object URL fed to AvatarCropModal, whose own output (the actual crop,
  // zoom and rotation the person chose) is what's sent to uploadAvatar.
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);

  const beforeUpload = (file: RcFile) => {
    if (!DEFAULT_ALLOWED_TYPES.includes(file.type)) {
      message.error(invalidTypeMessage || translate(t, TRANSLATIONS.AVATAR_UPLOAD_INVALID_TYPE));
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 >= DEFAULT_MAX_SIZE_MB) {
      message.error(tooLargeMessage || t(TRANSLATIONS.AVATAR_UPLOAD_TOO_LARGE, { maxSizeMB: DEFAULT_MAX_SIZE_MB }));
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleChange: UploadProps['onChange'] = ({ fileList }) => {
    const file = fileList[fileList.length - 1]?.originFileObj;
    if (!file) return;
    setCropImageSrc(URL.createObjectURL(file));
  };

  const closeCropModal = () => {
    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    setCropImageSrc(null);
  };

  const handleCropSave = async (croppedFile: File) => {
    closeCropModal();
    setUploading(true);
    try {
      const url = await uploadFn(croppedFile);
      onChange?.(url);
    } catch {
      message.error(uploadFailedMessage || translate(t, TRANSLATIONS.AVATAR_UPLOAD_FAILED));
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Upload
        accept={DEFAULT_ALLOWED_TYPES.join(',')}
        showUploadList={false}
        beforeUpload={beforeUpload}
        onChange={handleChange}
        disabled={disabled || uploading}
      >
        <div style={{ position: 'relative', width: size, height: size, cursor: disabled ? 'default' : 'pointer' }}>
          <Avatar size={size} src={value || undefined} icon={!value ? <UserOutlined /> : undefined} />
          {uploading ? (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0,0,0,0.35)',
                borderRadius: '50%',
              }}
            >
              <Spin size="small" />
            </div>
          ) : (
            !disabled && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: size * 0.32,
                  height: size * 0.32,
                  borderRadius: '50%',
                  background: token.colorPrimary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid #fff',
                }}
              >
                <CameraOutlined style={{ color: '#fff', fontSize: size * 0.15 }} />
              </div>
            )
          )}
        </div>
      </Upload>
      {/* Deliberately a sibling of Upload, not nested inside it -- antd's
          Upload attaches its "open the file picker" onClick to everything
          inside its own React subtree, and since Modal teleports its DOM
          node via a portal but NOT its React parent, a click on the
          slider/buttons here would still bubble up through Upload's click
          handler and reopen the file picker if this were nested. */}
      {cropImageSrc && (
        <AvatarCropModal open imageSrc={cropImageSrc} onCancel={closeCropModal} onSave={handleCropSave} />
      )}
    </>
  );
};

export default AvatarUpload;
